import { useLayoutEffect, useRef, useState } from "preact/hooks";

/**
 * The app's motion primitives. Four hooks, no dependencies.
 *
 * Everything here is transform/opacity only, so animation stays on the compositor
 * and never triggers layout — which matters on the phone-in-one-hand-on-a-fairway
 * hardware this app actually runs on.
 *
 * The stylesheet already neutralises CSS transitions under `prefers-reduced-motion`,
 * but none of that reaches work driven from JavaScript. Every hook below checks the
 * query itself and snaps straight to the final state when it's set.
 */
export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
}

/**
 * How long a row takes to travel, and how it spends that time.
 *
 * The curve matters more than the number here. The first attempt used
 * `cubic-bezier(0.2, 0.8, 0.2, 1)`, whose control point at y=0.8 put 80% of the
 * travel into the first 115ms of 420 — the row lurched, then crawled the rest, which
 * reads as a rendering fault rather than as movement. A row is at rest at both ends
 * of a reorder, so it wants to accelerate away and decelerate in: this is the
 * standard curve, 50% covered at 35% elapsed.
 *
 * How long the cues wait before joining it — see `useRecentDelta`'s `delay` — is
 * keyed off this, so the two stay in step.
 */
export const FLIP_MS = 700;
const FLIP_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

/**
 * FLIP: makes a reordered list animate from where it was to where it now is.
 *
 * Preact has already moved the rows by the time we can look, so there's nothing left
 * to animate forwards. Instead this records each row's position, and on the render
 * that reorders them inverts the change — putting each row back where it was with a
 * transform — then releases them on the next frame so they travel to their real
 * positions.
 *
 * `orderKey` is the list's running order (a joined list of ids). Two things depend
 * on it:
 *
 *  - Nothing animates unless the order actually changed. This screen re-renders
 *    roughly every frame while a number is counting up, and without that guard every
 *    one of those renders would look like a move.
 *  - While rows are in flight their measured positions are wherever the animation has
 *    got to, which is useless as a baseline for the next move — so measuring pauses
 *    until the current flight lands.
 *
 * Returns a ref factory: give each row `ref=${register(id)}` with a stable id, not
 * the index, which changes precisely when a row moves.
 */
export function useFlipList(orderKey: string): (key: string) => (el: HTMLElement | null) => void {
  const nodes = useRef(new Map<string, HTMLElement>());
  const previous = useRef(new Map<string, DOMRect>());
  const lastOrder = useRef(orderKey);
  const flights = useRef<Animation[]>([]);

  const measure = () => {
    const next = new Map<string, DOMRect>();
    for (const [key, el] of nodes.current) next.set(key, el.getBoundingClientRect());
    previous.current = next;
  };

  useLayoutEffect(() => {
    const orderChanged = orderKey !== lastOrder.current;
    lastOrder.current = orderKey;

    // A running transform changes what getBoundingClientRect reports, so a row in
    // flight can't provide a baseline. The render that launched it already recorded
    // the right one.
    flights.current = flights.current.filter((a) => a.playState === "running");
    if (flights.current.length > 0) return;

    if (prefersReducedMotion() || !orderChanged) {
      measure();
      return;
    }

    const before = previous.current;
    const moves: [HTMLElement, number][] = [];

    for (const [key, el] of nodes.current) {
      const was = before.get(key);
      if (!was) continue;
      const shift = was.top - el.getBoundingClientRect().top;
      // Sub-pixel drift from a late font or a scrollbar isn't a reorder.
      if (Math.abs(shift) < 1) continue;
      moves.push([el, shift]);
    }

    measure();

    // Web Animations rather than a transition on an inline style. Driving this with
    // `transition` means setting the inverted transform, forcing a reflow so the
    // browser takes it as a start value, then clearing it — and if the reflow lands
    // in the wrong place in the frame, the browser coalesces the two writes and the
    // rows teleport instead of travelling. animate() takes both keyframes at once,
    // so there is no start value to lose. It also leaves the element's own styles
    // alone, so it composes with the CSS entrance animation instead of fighting it.
    for (const [el, shift] of moves) {
      flights.current.push(
        el.animate({ transform: [`translateY(${shift}px)`, "none"] }, { duration: FLIP_MS, easing: FLIP_EASE }),
      );
    }
  });

  // Cleanup belongs on unmount only. The effect above deliberately has no dependency
  // array — it has to see every render to keep positions current — so a cleanup
  // returned from it would cancel every flight on the very next frame.
  useLayoutEffect(
    () => () => {
      for (const a of flights.current) a.cancel();
      flights.current = [];
    },
    [],
  );

  return (key: string) => (el: HTMLElement | null) => {
    if (el) nodes.current.set(key, el);
    else nodes.current.delete(key);
  };
}

/**
 * Tweens a number when it changes, so a score that moves is seen to move.
 *
 * Deliberately does not run on first mount: sweeping every figure up from zero each
 * time you open a screen is a gimmick, and it would make a static board look like
 * something just happened. It only animates a value that changed while you were
 * looking at it.
 */
export function useCountUp(value: number, ms = 900): number {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  const frame = useRef(0);

  useLayoutEffect(() => {
    if (from.current === value) return;

    const start = from.current;
    const delta = value - start;
    from.current = value;

    if (prefersReducedMotion()) {
      setShown(value);
      return;
    }

    const startedAt = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - startedAt) / ms);
      // easeOutQuad. Digits are read, not watched, so they need a steady cadence —
      // the cubic this replaced spent half its range in the first fifth of the
      // duration, which turned any sizeable jump into a blur followed by a crawl.
      const eased = 1 - (1 - t) * (1 - t);
      setShown(Math.round(start + delta * eased));
      if (t < 1) frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame.current);
  }, [value, ms]);

  return shown;
}

export interface RecentDeltaOptions {
  /** While false the baseline still tracks, but nothing is reported. */
  enabled?: boolean;
  /** How long to wait before reporting, so a cue can follow a movement instead of
   * competing with it. */
  delay?: number;
  /** How long the reported change stays up once it appears. */
  hold?: number;
}

/**
 * How much a number moved, for a while after it moved — 0 the rest of the time.
 *
 * The boolean `useChanged` says something happened; this says what. The standings
 * need the second one, because "you gained two places" and "you lost one" deserve
 * different cues.
 *
 * `delay` exists because a reorder used to fire everything at once — rows sliding,
 * numbers counting, tint arriving, chip popping in and shoving the points column
 * sideways. Four simultaneous changes read as a glitch. Holding the cue until the
 * rows have landed turns it into a sequence.
 */
export function useRecentDelta(value: number, options: RecentDeltaOptions = {}): number {
  const { enabled = true, delay = 0, hold = 2200 } = options;
  const [delta, setDelta] = useState(0);
  const previous = useRef(value);
  const wasEnabled = useRef(enabled);

  useLayoutEffect(() => {
    const change = value - previous.current;
    previous.current = value;
    // The render that arms the hook is usually the same one that brings the real
    // numbers in, so its change is the screen finishing loading rather than anything
    // moving. Arming re-seeds the baseline and reports nothing.
    const justArmed = enabled && !wasEnabled.current;
    wasEnabled.current = enabled;

    // While disabled the baseline keeps tracking; only the cue is suppressed.
    if (!enabled || justArmed || change === 0 || prefersReducedMotion()) return;

    const show = setTimeout(() => setDelta(change), delay);
    const clear = setTimeout(() => setDelta(0), delay + hold);
    return () => {
      clearTimeout(show);
      clearTimeout(clear);
    };
  }, [value, enabled, delay, hold]);

  return delta;
}

/**
 * True for `ms` after `value` changes — for one-shot flashes and pops.
 *
 * Stays false on first mount for the same reason `useCountUp` doesn't tween there:
 * arriving at a screen isn't a change worth flagging.
 */
export function useChanged(value: unknown, ms = 2200): boolean {
  const [active, setActive] = useState(false);
  const previous = useRef(value);

  useLayoutEffect(() => {
    if (previous.current === value) return;
    previous.current = value;

    if (prefersReducedMotion()) return;

    setActive(true);
    const timer = setTimeout(() => setActive(false), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);

  return active;
}
